import styled from "@emotion/styled";
import React from "react";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { AppIcon } from "../../Generic/AppIcon";
import { AppList, AppListItem, ListItemBullet } from "../../Generic/AppList";
import { If } from "../../Generic/If";
import { isDefined } from "../../Generic/IsDefined";
import { Slide, SlideProps, SlideStepLabel, SlideTitle } from "./Slide";
import { default as PatientAllergiesIconSvg } from "/public/assets/patient-allergies-icon.svg?react";
import { default as PatientMedicalInformationIconSvg } from "/public/assets/patient-medical-information-icon.svg?react";
import { default as PatientRequestsIconSvg } from "/public/assets/patient-requests-icon.svg?react";
import { default as PatientTasksIconSvg } from "/public/assets/patient-tasks-icon.svg?react";

export const Slide3CaseInfo = (props: SlideProps) => {
  const { patientRequests, patientTasks, patientMedicalNotes, patientAllergies, generalNotes } =
    props.visit.publicVisitBroadcastDetails;

  const allergies = patientAllergies.map(({ name }) => name).filter(isDefined);

  return (
    <Slide>
      <If condition={props.showSteps}>
        <SlideStepLabel {...props} />
      </If>
      <SlideTitle>{translate(STRINGS.CASE_INFO)}</SlideTitle>
      <CaseInfosContainer>
        <CaseInfoListCard
          title={translate(STRINGS.PATIENT_REQUESTS)}
          items={patientRequests}
          icon={<PatientTasksIconSvg />}
        />

        <CaseInfoListCard
          title={translate(STRINGS.MAIN_TASKS)}
          items={patientTasks}
          icon={<PatientTasksIconSvg />}
        />

        <CaseInfoSingleCard
          title={translate(STRINGS.GENERAL_NOTES)}
          caseInformation={generalNotes}
          icon={<PatientRequestsIconSvg />}
        />

        <CaseInfoSingleCard
          title={translate(STRINGS.MEDICAL_INFORMATION)}
          caseInformation={patientMedicalNotes}
          icon={<PatientMedicalInformationIconSvg />}
        />

        <CaseInfoListCard
          title={translate(STRINGS.ALLERGIES)}
          items={allergies}
          icon={<PatientAllergiesIconSvg />}
          bulletColor="#3D3D3D"
          bulletBackground="#FEF2F2"
        />
      </CaseInfosContainer>
    </Slide>
  );
};

interface CaseInfoCardProps {
  icon: JSX.Element;
  title: string;
}

interface CaseInfoSingleCardProps extends CaseInfoCardProps {
  caseInformation: string | null;
}

const CaseInfoSingleCard = ({ icon, title, caseInformation }: CaseInfoSingleCardProps) => {
  if (!isDefined(caseInformation) || caseInformation.length === 0) {
    return null;
  }

  return (
    <CaseInfoAppList className="SingleTextCard">
      <CaseInfoAppListTitle icon={icon} title={title} />
      <AppListItem title={<CardInfoValue>{caseInformation}</CardInfoValue>} />
    </CaseInfoAppList>
  );
};

interface CaseInfoListCardProps extends CaseInfoCardProps {
  items: string[];
  bulletColor?: string;
  bulletBackground?: string;
}

const CaseInfoListCard = (props: CaseInfoListCardProps) => {
  const { icon, title, items, bulletColor, bulletBackground } = props;

  if (items.length === 0) {
    return null;
  }

  return (
    <CaseInfoAppList>
      <CaseInfoAppListTitle icon={icon} title={title} />
      {items.map((item, index) => (
        <CaseInfoAppListItem
          key={`${item}-${index}`}
          item={item}
          index={index}
          bulletColor={bulletColor}
          bulletBackground={bulletBackground}
        />
      ))}
    </CaseInfoAppList>
  );
};

interface CaseInfoAppListTitleProps {
  title: string;
  icon: JSX.Element;
}

const CaseInfoAppListTitle = ({ title, icon }: CaseInfoAppListTitleProps) => {
  const bullet = (
    <ListItemBullet background="transparent">
      <AppIcon icon={icon} />
    </ListItemBullet>
  );

  return <AppListItem leading={bullet} title={<CardInfoTitle>{title}</CardInfoTitle>} />;
};

interface CaseInfoAppListItemProps {
  item: string;
  index: number;
  bulletColor?: string;
  bulletBackground?: string;
}

const CaseInfoAppListItem = (props: CaseInfoAppListItemProps) => {
  const { item, index, bulletColor, bulletBackground } = props;

  const bullet = (
    <ListItemBullet color={bulletColor} background={bulletBackground}>
      {index + 1}
    </ListItemBullet>
  );

  return (
    <AppListItem
      key={`${item}-${index}`}
      leading={bullet}
      title={<CardInfoValue>{item}</CardInfoValue>}
    />
  );
};

const CaseInfosContainer = styled.div`
  padding-top: 2px;
`;

const CardInfoTitle = styled.div`
  font-weight: 900;
  font-size: 16px;
  line-height: 22px;
`;

const CardInfoValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #53606c;
`;

const CaseInfoAppList = styled(AppList)`
  & + & {
    margin-top: 18px;
  }

  &.SingleTextCard ${CardInfoValue} {
    margin-left: 12px;
  }
`;
